import { useCallback } from "react";
import { useLocation } from "react-router";
import { clientScopeOf } from "../shell/modes/clientScope.ts";

/**
 * Where a task's full page is (015 `contracts/screens.md` § Route map, 2026-10-07, 018 FR-018-238's
 * pattern for meetings applied to tasks): under the client whose pages it is opened from, at
 * `/{ws}/clients/{account}/tasks/{item}`, so the rail stays on Clients and the crumbs lead back through
 * the client; from the Clients area's other pages (Client tasks, the Library, Unassigned emails) under
 * the task's own client where it reports one; anywhere else at `/{ws}/tasks/{item}`. Both addresses
 * draw one page from one read. A link someone copies to share stays the second, the task's own address
 * wherever it is read.
 */
export function taskPath(ws: string, identifier: string, accountId: string | null = null): string {
  const item = encodeURIComponent(identifier);
  return accountId === null ? `/${ws}/tasks/${item}` : `/${ws}/clients/${encodeURIComponent(accountId)}/tasks/${item}`;
}

/**
 * The full page of a task as opened from the page on screen: under the client whose pages these are,
 * under `client`, the task's own client where the caller knows it, elsewhere in the Clients area, and the
 * Team's otherwise.
 */
export function useTaskPath(ws: string): (identifier: string, client?: string | null) => string {
  const { pathname } = useLocation();
  const { clients, accountId } = clientScopeOf(ws, pathname);
  return useCallback((identifier: string, client: string | null = null) => taskPath(ws, identifier, accountId ?? (clients ? client : null)), [ws, clients, accountId]);
}
